import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  MoreHorizontal,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  UserCog,
} from 'lucide-react';
import type { AdminRow } from '@runik/shared';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { DataTable } from '../../../../components/data/DataTable';
import { EmptyState } from '../../../../components/data/EmptyState';
import { AdminFormDialog } from '../../../../components/admins/AdminFormDialog';
import { toast } from '../../../../components/ui/sonner';
import { ApiError } from '../../../../lib/auth/client';
import { adminsKey, deleteAdmin, listAdmins } from '../../../../lib/queries/admins';

function AdminsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminRow | null>(null);

  const query = { page, limit: 25 };
  const admins = useQuery({
    queryKey: adminsKey(query),
    queryFn: () => listAdmins(query),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteAdmin(id),
    onSuccess: () => {
      toast.success('Yönetici silindi');
      qc.invalidateQueries({ queryKey: ['admins'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Silme başarısız');
    },
  });

  const columns = useMemo<ColumnDef<AdminRow, unknown>[]>(
    () => [
      {
        accessorKey: 'email',
        header: 'E-posta',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.original.email}</span>
            {row.original.isSelf && (
              <Badge variant="secondary" className="text-[10px]">
                siz
              </Badge>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Oluşturulma',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'd MMM yyyy', { locale: tr }),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Son Güncelleme',
        cell: ({ row }) => format(new Date(row.original.updatedAt), 'd MMM yyyy HH:mm', { locale: tr }),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const a = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditing(a)}>
                  <Pencil className="h-4 w-4" /> Düzenle
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                  disabled={a.isSelf}
                  onSelect={() => !a.isSelf && setPendingDelete(a)}
                >
                  <Trash2 className="h-4 w-4" /> Sil
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Yöneticiler"
        description="Admin kullanıcıları"
        actions={
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="h-4 w-4" /> Yeni Yönetici
          </Button>
        }
      />
      <div className="p-6">
        <DataTable
          columns={columns}
          data={admins.data?.data}
          loading={admins.isLoading}
          getRowId={(row) => row.id}
          empty={
            <EmptyState
              icon={UserCog}
              title="Yönetici yok"
              description="İlk admin kullanıcısını oluşturarak başlayın."
              action={
                <Button onClick={() => setFormOpen(true)}>
                  <Plus className="h-4 w-4" /> Yönetici Ekle
                </Button>
              }
            />
          }
          pagination={
            admins.data
              ? {
                  page: admins.data.page,
                  limit: admins.data.limit,
                  total: admins.data.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>

      <AdminFormDialog open={formOpen} onOpenChange={setFormOpen} />

      {editing && (
        <AdminFormDialog
          open
          onOpenChange={(o) => !o && setEditing(null)}
          admin={editing}
        />
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-500" /> Yönetici silinsin mi?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{pendingDelete?.email}</strong> kalıcı olarak silinecek. Tüm aktif oturumları
              kapanır. Son admin'i silmek mümkün değil — sistem en az bir yönetici tutar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) remove.mutate(pendingDelete.id);
                setPendingDelete(null);
              }}
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export const Route = createFileRoute('/_app/admin/admins/')({ component: AdminsPage });
